import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import api, { getApiError } from '../lib/axios';
import {
  Gavel, Loader2, ArrowLeft, Check, X, Award, ShieldCheck, Sparkles,
  ExternalLink, CheckCircle2, XCircle, Flame,
} from 'lucide-react';
import type { CampaignDetail as CampaignDetailType, Bid, CampaignStatus } from '../types';

const STATUS_STYLES: Record<CampaignStatus, string> = {
  draft:     'bg-slate-100 text-slate-700 border-slate-200',
  open:      'bg-blue-50 text-blue-700 border-blue-200',
  awarded:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  closed:    'bg-slate-100 text-slate-500 border-slate-200',
  completed: 'bg-teal-50 text-teal-700 border-teal-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<CampaignDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    try {
      const res = await api.get(`/api/campaigns/${id}`);
      setCampaign(res.data);
      setError(null);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (id) load(); }, [id]);

  const publish = async () => {
    if (!campaign) return;
    setBusy('publish');
    try {
      await api.post(`/api/campaigns/${campaign.id}/publish`);
      await load();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const accept = async (bid: Bid) => {
    if (!campaign) return;
    if (!confirm(`Accept this bid? You'll be taken to ${bid.seller_name} to complete the purchase for $${bid.price}.`)) return;
    setBusy(bid.id);
    try {
      await api.post(`/api/campaigns/${campaign.id}/bids/${bid.id}/accept`);
      await load();
      // Open seller's website in a new tab for the buyer to complete purchase
      if (bid.seller_website) {
        window.open(bid.seller_website, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const reject = async (bid: Bid) => {
    if (!campaign) return;
    const reason = prompt('Optional: reason for rejecting this bid?') || '';
    setBusy(bid.id);
    try {
      await api.post(`/api/campaigns/${campaign.id}/bids/${bid.id}/reject`, { reason });
      await load();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const close = async () => {
    if (!campaign) return;
    if (!confirm('Close this campaign? Pending bids will remain visible but you can no longer accept new ones.')) return;
    setBusy('close');
    try {
      await api.post(`/api/campaigns/${campaign.id}/close`);
      await load();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const complete = async () => {
    if (!campaign) return;
    setBusy('complete');
    try {
      await api.post(`/api/campaigns/${campaign.id}/complete`);
      await load();
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setBusy(null);
    }
  };

  const sortedBids = useMemo(() => campaign?.bids ?? [], [campaign]);

  if (loading) {
    return <DashboardLayout><div className="p-12 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div></DashboardLayout>;
  }
  if (!campaign) {
    return <DashboardLayout><div className="p-8 text-center text-slate-500">{error || 'Campaign not found.'}</div></DashboardLayout>;
  }

  const bestActivePrice = sortedBids
    .filter(b => b.status === 'pending' || b.status === 'accepted')
    .reduce<number | null>((acc, b) => acc == null || b.price < acc ? b.price : acc, null);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/campaigns" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> All campaigns
        </Link>

        <header className="mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${STATUS_STYLES[campaign.status]}`}>
                  {campaign.status}
                </span>
                <span className="text-xs text-slate-500">{new Date(campaign.created_at).toLocaleDateString()}</span>
              </div>
              <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <Gavel className="w-5 h-5 text-brand-600" /> {campaign.title}
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                Budget ≤ ${campaign.budget_max_per_link}/link · {campaign.links_needed} needed · {campaign.bid_count} bid{campaign.bid_count === 1 ? '' : 's'} ({campaign.accepted_bid_count} accepted)
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {campaign.status === 'draft' && (
                <button onClick={publish} disabled={busy === 'publish'}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-semibold rounded-xl shadow-sm shadow-brand-500/25 disabled:opacity-50">
                  {busy === 'publish' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Publish & collect bids
                </button>
              )}
              {campaign.status === 'open' && (
                <button onClick={close} disabled={busy === 'close'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-xl hover:bg-slate-50 disabled:opacity-50">
                  Close campaign
                </button>
              )}
              {campaign.status === 'awarded' && (
                <button onClick={complete} disabled={busy === 'complete'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold rounded-xl disabled:opacity-50">
                  <CheckCircle2 className="w-4 h-4" /> Mark complete
                </button>
              )}
            </div>
          </div>
        </header>

        {/* Brief summary */}
        <section className="mb-6 p-5 bg-white border border-slate-200 rounded-2xl">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <Kv label="Keyword" value={campaign.target_keyword} />
            <Kv label="Target URL" value={campaign.target_url} link />
            <Kv label="DR range" value={[campaign.min_dr, campaign.max_dr].filter(v => v != null).join(' – ') || 'any'} />
            <Kv label="Min traffic" value={campaign.min_traffic?.toLocaleString() || 'any'} />
            <Kv label="Country" value={campaign.country || 'any'} />
            <Kv label="Language" value={campaign.language || 'any'} />
            <Kv label="Link type" value={campaign.listing_type || 'any'} />
            <Kv label="Dofollow" value={campaign.dofollow_required ? 'required' : 'optional'} />
          </div>
          {campaign.description && (
            <p className="mt-4 pt-4 border-t border-slate-100 text-sm text-slate-600 leading-relaxed whitespace-pre-line">
              {campaign.description}
            </p>
          )}
        </section>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-700">{error}</div>
        )}

        {/* Bids */}
        <section>
          <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
            <Flame className="w-5 h-5 text-brand-600" /> Bids
            {campaign.status === 'open' && <span className="text-xs font-normal text-slate-500">live auction</span>}
          </h2>

          {campaign.status === 'draft' ? (
            <div className="p-10 text-center bg-white border border-dashed border-slate-300 rounded-2xl">
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                This campaign is still a draft. Click <strong>Publish</strong> above to open it for bidding.
                As soon as it goes live, auto-bids from matching marketplaces will stream in.
              </p>
            </div>
          ) : sortedBids.length === 0 ? (
            <div className="p-10 text-center bg-white border border-slate-200 rounded-2xl">
              <p className="text-sm text-slate-500">
                No bids match your requirements yet. You may need to raise the budget or relax the filters.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedBids.map(bid => (
                <BidCard
                  key={bid.id}
                  bid={bid}
                  bestPrice={bestActivePrice}
                  canDecide={campaign.status === 'open' || (campaign.status === 'awarded' && campaign.accepted_bid_count < campaign.links_needed)}
                  busy={busy === bid.id}
                  onAccept={() => accept(bid)}
                  onReject={() => reject(bid)}
                />
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function Kv({ label, value, link }: { label: string; value: any; link?: boolean }) {
  const text = value == null || value === '' ? 'any' : String(value);
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{label}</div>
      {link && value ? (
        <a href={value} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-brand-600 hover:underline inline-flex items-center gap-1">
          {text.replace(/^https?:\/\//, '')} <ExternalLink className="w-3 h-3" />
        </a>
      ) : (
        <div className="text-sm font-medium text-slate-900">{text}</div>
      )}
    </div>
  );
}

function BidCard({
  bid, bestPrice, canDecide, busy, onAccept, onReject,
}: {
  bid: Bid; bestPrice: number | null; canDecide: boolean; busy: boolean;
  onAccept: () => void; onReject: () => void;
}) {
  const isBest = bestPrice != null && bid.price === bestPrice && (bid.status === 'pending' || bid.status === 'accepted');
  const isFeatured = bid.seller_featured;
  const statusColor =
    bid.status === 'accepted' ? 'bg-emerald-50 border-emerald-200' :
    bid.status === 'rejected' ? 'bg-slate-50 border-slate-200 opacity-60' :
    'bg-white border-slate-200';

  return (
    <div className={`p-5 border rounded-2xl ${statusColor} ${isBest ? 'ring-2 ring-brand-500/20' : ''}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap gap-1.5 mb-2">
            {isFeatured && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-800">
                <Sparkles className="w-3 h-3" /> Recommended
              </span>
            )}
            {isBest && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-brand-100 text-brand-800">
                <Award className="w-3 h-3" /> Best price
              </span>
            )}
            {bid.status === 'accepted' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-emerald-100 text-emerald-800">
                <CheckCircle2 className="w-3 h-3" /> Accepted
              </span>
            )}
            {bid.status === 'rejected' && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-slate-200 text-slate-600">
                <XCircle className="w-3 h-3" /> Rejected
              </span>
            )}
            {bid.source === 'auto' && (
              <span className="inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-slate-100 text-slate-500">
                Auto-bid
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <h3 className="text-base font-bold text-slate-900">{bid.domain}</h3>
            {bid.seller_verified && <ShieldCheck className="w-4 h-4 text-brand-500" />}
            <span className="text-xs text-slate-500">from <strong>{bid.seller_name}</strong></span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600 mt-1">
            {bid.dr != null && <span>DR {bid.dr}</span>}
            {bid.da != null && <span>DA {bid.da}</span>}
            {bid.organic_traffic != null && <span>{bid.organic_traffic.toLocaleString()} traffic/mo</span>}
            {bid.country && <span>{bid.country}</span>}
            {bid.category && <span>{bid.category}</span>}
            <span className="capitalize">{bid.listing_type.replace('_', ' ')}</span>
          </div>
          {bid.notes && <p className="text-xs text-slate-500 italic mt-2">"{bid.notes}"</p>}
          {bid.rejection_reason && <p className="text-xs text-rose-600 mt-2">Rejected: {bid.rejection_reason}</p>}
        </div>

        <div className="text-right min-w-[140px]">
          <div className="text-2xl font-extrabold text-slate-900">${bid.price.toFixed(0)}</div>
          {bid.discount_vs_catalog_pct && bid.discount_vs_catalog_pct > 0 && (
            <div className="text-[11px] text-emerald-600 font-semibold mb-2">{bid.discount_vs_catalog_pct}% below catalog</div>
          )}
          {canDecide && bid.status === 'pending' && (
            <div className="flex gap-2 justify-end mt-2">
              <button
                onClick={onReject}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg disabled:opacity-50 inline-flex items-center gap-1"
              >
                <X className="w-3 h-3" /> Reject
              </button>
              <button
                onClick={onAccept}
                disabled={busy}
                className="px-3 py-1.5 text-xs font-bold text-white bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 rounded-lg disabled:opacity-50 inline-flex items-center gap-1"
              >
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Accept
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
